/**
 * Integrations access gate — tiered while platform reviews are pending:
 *   • Allowlisted test accounts → EVERY provider (full AVAILABLE/PUBLISHABLE lists).
 *   • Everyone else → PUBLIC_PROVIDERS: LinkedIn, YouTube, Google Drive, TikTok
 *     (audit passed 2026-08-01) and Facebook/Instagram/Threads (Meta app review
 *     approved 2026-09-05). Only X still hides behind the allowlist (publish TODO);
 *     unreleased platforms render as "coming soon" cards with their real icons.
 * Widen PUBLIC_PROVIDERS in social.ts as each platform's review lands.
 */
import { useEffect, useState } from 'react';
import { useUserStore } from '@/store';
import {
  AVAILABLE_PROVIDERS,
  PUBLISHABLE_PROVIDERS,
  PUBLIC_PROVIDERS,
  type SocialProvider,
} from '@/v2/social';

// SHA-256 of the allowlisted emails: the bundle must not publish staff addresses.
const ALLOWLIST_SHA256 = new Set([
  '55cf486b9940500202458b53317332dbdcc6e38dbdd3b4408eb2db312bae4d8b',
  '7c736cd3ef7e8de0611094898a71b5427b60c8e723cf8b910b28bf231ad1e162',
  'dd42a4eb2b4bdb100fcaec8140cbfa2e4610c015e00217ae9752527e9fd8a8eb',
  '077d78151df7edddac004907dcb576a808a3bb0950bec9238148d92db1338981',
  '0854f42204c64011a0ff084f7034b9892f2728f45d7e54ec554f91476d459904', // YouTube + Google Drive OAuth-verification demo/testing
]);

const sha256Hex = async (text: string): Promise<string> => {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
};

export const isIntegrationsAllowed = async (email?: string | null): Promise<boolean> =>
  !!email && ALLOWLIST_SHA256.has(await sha256Hex(email.trim().toLowerCase()));

/** Hook: is the signed-in user on the full-access allowlist? */
export const useIntegrationsAllowed = (): boolean => {
  const email = useUserStore((s) => s.user?.email);
  const [allowed, setAllowed] = useState(false);
  useEffect(() => {
    let live = true;
    isIntegrationsAllowed(email).then((ok) => { if (live) setAllowed(ok); }, () => {});
    return () => { live = false; };
  }, [email]);
  return allowed;
};

/** Providers this tier can CONNECT (others show as "coming soon"). */
export const availableProvidersFor = (allowed: boolean): readonly SocialProvider[] =>
  allowed ? AVAILABLE_PROVIDERS : PUBLIC_PROVIDERS;

/** Providers this tier can PUBLISH a finished video to. */
export const publishableProvidersFor = (allowed: boolean): readonly SocialProvider[] =>
  allowed ? PUBLISHABLE_PROVIDERS : PUBLIC_PROVIDERS;
